
import React from 'react';
import _ from 'lodash';

import Joi from 'joi';
import Form from '../../common/form';
import Button from '../../themed/button';
import Conditional from '../../common/conditional';

import { func, object } from 'prop-types';
import { getFileUploadTypes, matchPhrases } from '../../../utils/utilities';
import { getDefectTypes, getResultOptionsForDRTReport } from '../../../utils/data';

import toastService from '../../../services/toastService';

class PAUTScanData extends Form {
	state = {
		data: {
			weldNumber: '',
            scanNumber: '',
            aScan: [],
            sScan: [],
            cScan: [],
            thickness: 0,
            pipeDia: 0,
            defectType: '',
            defectDepth: 0,
            defectLength: 0,
            defectHeight: 0,
            scanStart: 0,
            scanEnd: 0,
            minimumMeasuredThickness: 0,
            result: '',
            limitation: '',
            remark: '',
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
        weldNumber: Joi.string().required().label('Weld Number'),
        scanNumber: Joi.string().required().label('Scan Number'),
        aScan: Joi.any().when('result', {
            is: 'Restrictions',
            then: Joi.array().min(0).max(1).required().label('A Scan'),
            otherwise: Joi.array().min(1).max(1).required().label('A Scan'),
        }),
        sScan: Joi.any().when('result', {
            is: 'Restrictions',
            then: Joi.array().min(0).max(1).required().label('S Scan'),
            otherwise: Joi.array().min(1).max(1).required().label('S Scan'),
        }),
        cScan: Joi.any().when('result', {
            is: 'Restrictions',
            then: Joi.array().min(0).max(1).required().label('C Scan'),
            otherwise: Joi.array().min(1).max(1).required().label('C Scan'),
        }),
		thickness: Joi.number().positive().required().label('Thickness (mm)'),
		pipeDia: Joi.number().positive().required().label('Pipe Dia (inch)'),
        defectType: Joi.alternatives().try(Joi.array().min(1).items(Joi.object({
			id: Joi.string().required(),
			name: Joi.string().required(),
		})), Joi.string().min(3)).required().label('Defect Type'),
        defectDepth: Joi.alternatives().try(Joi.number().positive(), Joi.string().valid('N/A').insensitive()).required().label('Defect Depth (mm)'),
		defectLength: Joi.alternatives().try(Joi.number().positive(), Joi.string().valid('N/A').insensitive()).required().label('Defect Length (mm)'),
		defectHeight: Joi.alternatives().try(Joi.number().positive(), Joi.string().valid('N/A').insensitive()).required().label('Defect Height (mm)'),
        scanStart: Joi.number().required().label('Scan Start(mm'),
        scanEnd: Joi.number().required().label('Scan End(mm'),
        minimumMeasuredThickness: Joi.number().min(0).required().label('Minimum measured thickness (mm)'),
        result: Joi.string().required().label('Result'),
        limitation: Joi.string().required().label('limitation'),
        remark: Joi.string().required().label('remark'),
	};

	static propTypes = {
        initialPautItem: object,
        onDeleteItem: func,
		onSave: func,
	};

    componentDidMount(): void {
        if (this.props.initialPautItem) {
            this.setState({ data: this.props.initialPautItem });
        }
    }

    componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{ data: any; errors: any; progress: number; loading: boolean; touched?: boolean; }>, snapshot?: any): void {
        if (prevState.data.result === this.state.data.result) return;
    
        const data = { ...this.state.data } as any;

        if (data.result && _.isString(data.defectType)) {
            if (matchPhrases(data.result, 'rejected')) {
                data.defectType = data.defectType.split(', ').map((value: string) => ({ id: value, name: value }));
            }

            if (this.getResultIsRestricted()) {
                data.defectType = 'N/A';
                data.defectDepth = 'N/A';
                data.defectLength = 'N/A';
                data.defectHeight = 'N/A';
            }
        }

        this.setState({ data });
    }

	doSubmit = (): void => {
		this.props.onSave(this.getApiData());
        if (!this.props.initialPautItem) this.resetData();
        toastService.success('Changes saved!')
	};

    getApiData = () => {
        if (_.isArray(this.state.data.defectType)) {
            const defectType = this.state.data.defectType.map((option) => option.id).join(', ');
            return { ...this.state.data, defectType };
        }

        return this.state.data;
    };

    getResultIsRestricted = () => {
        return matchPhrases(this.state.data.result, 'restrictions');
    };

    resetData = () => {
        this.setState({
            data: {
                weldNumber: '',
                scanNumber: '',
                aScan: [],
                sScan: [],
                cScan: [],
                thickness: 0,
                pipeDia: 0,
                defectType: '',
                defectDepth: 0,
                defectLength: 0,
                defectHeight: 0,
                scanStart: 0,
                scanEnd: 0,
                minimumMeasuredThickness: 0,
                result: '',
                limitation: '',
                remark: '',
            },
        });
    };

    renderDefectType = () => {
        if (this.state.data.result && matchPhrases(this.state.data.result, 'rejected')) {
            return this.renderMultiSelect({
                name: 'defectType',
                label: 'Defect Type',
                placeholder: 'Select Defect Type',
                showCheckbox: true,
				loading: false,
                options: getDefectTypes().map((type) => ({ id: type, name: type })),
            });
        }

        return this.renderInput({
            type: 'text',
            name: 'defectType',
            label: 'Defect Type',
            placeholder: 'Enter Defect Type',
            disabled: this.getResultIsRestricted(),
        })
    };

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<h3 className="form-title">Scan Data Item</h3>

                <div className="container">
                    <div className="row">
                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'weldNumber',
                                label: 'Weld Number',
                                placeholder: 'Enter Weld Number',
                            })}
                        </div>
                        
                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'scanNumber',
                                label: 'Scan Number',
                                placeholder: 'Enter Scan Number',
                            })}
                        </div>

                        <div className="col">
                            {this.renderSelect({
                                name: 'result',
                                label: 'Result',
                                placeholder: 'Select Result',
                                options: getResultOptionsForDRTReport(),
                            })}
                        </div>

                        <div className="col">
                            {this.renderDefectType()}
                        </div>
                    </div>

                    <div className="divider my-4"></div>
                    
                    <div className="row">
                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'minimumMeasuredThickness',
                                label: 'Minimum measured thickness (MM)',
                                placeholder: 'Enter Minimum measured thickness',
                            })}
                        </div>
                        
                        <div className="col">
                            {this.renderInput({
                                type: 'number',
                                name: 'scanStart',
                                label: 'Scan Start (MM)',
                                placeholder: 'Enter Scan Start',
                            })}
                        </div>
                       
                        <div className="col">
                            {this.renderInput({
                                type: 'number',
                                name: 'scanEnd',
                                label: 'Scan End (MM)',
                                placeholder: 'Enter Scan End',
                            })}
                        </div>

                    </div>

                    <div className="divider my-4"></div>

                    <div className="row">
                        <div className="col">
                            {this.renderFileInput({
                                max: 1,
                                multiple: false,
                                name: 'aScan',
                                accept: 'image/*',
                                label: 'A Scan Image',
                                sublabel: 'Upload A Scan Image',
                                types: getFileUploadTypes(),
                            })}
                        </div>
                        <div className="col">
                            {this.renderFileInput({
                                max: 1,
                                multiple: false,
                                name: 'sScan',
                                accept: 'image/*',
                                label: 'S Scan Image',
                                sublabel: 'Upload S Scan Image',
                                types: getFileUploadTypes(),
                            })}
                        </div>
                        <div className="col">
                            {this.renderFileInput({
                                max: 1,
                                multiple: false,
                                name: 'cScan',
                                accept: 'image/*',
                                label: 'C Scan Image',
                                sublabel: 'Upload C Scan Image',
                                types: getFileUploadTypes(),
                            })}
                        </div>
                    </div>

                    <div className="divider my-4"></div>

                    <div className="row">
                        <div className="col">
                            {this.renderInput({
                                type: 'number',
                                name: 'thickness',
                                label: 'Thickness (mm)',
                                placeholder: 'Enter Thickness',
                            })}
                        </div>

                        <div className="col">
                            {this.renderInput({
                                type: 'number',
                                name: 'pipeDia',
                                label: 'Pipe Dia (Inch)',
                                placeholder: 'Enter Pipe Dia',
                            })}
                        </div>
                        
                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'defectDepth',
                                label: 'Defect Depth',
                                placeholder: 'Enter Defect Depth (mm)',
                                disabled: this.getResultIsRestricted()
                            })}
                        </div>
                    
                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'defectLength',
                                label: 'Defect Length (mm)',
                                placeholder: 'Enter Defect Length (mm)',
                                disabled: this.getResultIsRestricted()
                            })}
                        </div>
                    
                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'defectHeight',
                                label: 'Defect Height',
                                placeholder: 'Enter Defect Height (mm)',
                                disabled: this.getResultIsRestricted()
                            })}
                        </div>
                    </div>

                    <div className="divider my-4"></div>

                    <div className="row">
                        <div className="col">
                            {this.renderTextEditor({
                                name: 'limitation',
                                label: 'Limitation',
                                placeholder: 'Enter Limitation',
                            })}
                        </div>
                    
                        <div className="col">
                            {this.renderTextEditor({
                                name: 'remark',
                                label: 'Remark',
                                placeholder: 'Enter Remark',
                            })}
                        </div>
                    </div>
                </div>

                <div className="divider my-4"></div>

                <div className="d-flex justify-content-end">
                    <Conditional isVisible={!!this.props.onDeleteItem}>
                        <div className="me-3">
                            <Button
                                onClick={this.props.onDeleteItem} 
                                icon="trash"
                                type="button"
                                label="Delete" 
                                style={{ backgroundColor: 'red' }} 
                            />
                        </div>
                    </Conditional>

				    {this.renderButton('Save')}
                </div>
			</form>
		);
	}
}



export default PAUTScanData;
